import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig, debounce } from '@theme/utilities';

/**
 * "Get One, Gift One" GWP promo sync.
 *
 * Keeps a hidden "gift" line item (same variant as the paid line, flagged via
 * Theme.gwpPromo.giftPropertyKey) in sync with the customer's paid quantity,
 * capped at Theme.gwpPromo.maxFreeQuantity. The actual $0 pricing comes from
 * an automatic Buy X/Get Y discount configured in the Shopify admin; this
 * script only manages line-item quantity, not price.
 *
 * The feature is toggled via the "Enable GWP promo" theme setting (Theme
 * Customizer > Theme settings > GWP Promo). Note this listener still runs
 * while disabled: if a merchant turns the promo off mid-flight, any gift
 * line already sitting in a customer's cart needs to be removed, since it's
 * no longer backed by an active discount.
 */
const GWP_SOURCE = 'gwp-sync';

let syncing = false;
let queued = false;

/** @returns {string} */
function giftPropertyKey() {
  return Theme.gwpPromo?.giftPropertyKey ?? '_gwp_free_gift';
}

/** @returns {boolean} */
function isPromoActive() {
  return Boolean(Theme.gwpPromo?.enabled && Theme.gwpPromo?.productId);
}

/** @returns {Promise<any>} */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/** @returns {string[]} */
function getSectionIdsToUpdate() {
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.sectionId) {
      sectionIds.add(element.dataset.sectionId);
    }
  });
  return Array.from(sectionIds);
}

/**
 * Groups the cart's paid (non-gift) quantity of the active promo product by variant.
 * @param {any} cart
 * @returns {Map<number, number>}
 */
function getPaidQuantityByVariant(cart) {
  const propertyKey = giftPropertyKey();
  const paidQuantityByVariant = new Map();
  for (const item of cart.items) {
    if (item.product_id !== Theme.gwpPromo.productId || item.properties?.[propertyKey]) continue;
    paidQuantityByVariant.set(item.variant_id, (paidQuantityByVariant.get(item.variant_id) ?? 0) + item.quantity);
  }
  return paidQuantityByVariant;
}

/**
 * @param {any} cart
 * @param {number} variantId
 * @returns {any | undefined}
 */
function findGiftLine(cart, variantId) {
  const propertyKey = giftPropertyKey();
  return cart.items.find((item) => item.variant_id === variantId && item.properties?.[propertyKey]);
}

/**
 * @param {{ line: any, quantity: number, sectionIds: string[] }} config
 */
async function changeGiftLineQuantity({ line, quantity, sectionIds }) {
  const response = await fetch(Theme.routes.cart_change_url, {
    ...fetchConfig('json', {
      body: JSON.stringify({ id: line.key, quantity, sections: sectionIds.join(',') }),
    }),
  });
  return response.json();
}

/**
 * @param {{ variantId: number, quantity: number, sectionIds: string[] }} config
 */
async function addGiftLine({ variantId, quantity, sectionIds }) {
  const response = await fetch(Theme.routes.cart_add_url, {
    ...fetchConfig('json', {
      body: JSON.stringify({
        id: variantId,
        quantity,
        properties: { [giftPropertyKey()]: 'true' },
        sections: sectionIds.join(','),
      }),
    }),
  });
  return response.json();
}

/**
 * Reconciles free gift line quantities against paid quantities (or, when the
 * promo is disabled, tears down any gift lines left over from before it was
 * turned off), then re-renders affected sections and notifies the theme.
 */
async function syncGiftLines() {
  if (syncing) {
    queued = true;
    return;
  }
  syncing = true;

  try {
    const cart = await fetchCart();
    const promoActive = isPromoActive();
    const paidQuantityByVariant = promoActive ? getPaidQuantityByVariant(cart) : new Map();

    // Track every variant that either has paid quantity toward the active
    // promo, or already has a gift line (which may be an orphan left over
    // from a disabled/changed promo and needs cleaning up either way).
    const variantIds = new Set(paidQuantityByVariant.keys());
    for (const item of cart.items) {
      if (item.properties?.[giftPropertyKey()]) variantIds.add(item.variant_id);
    }

    if (variantIds.size === 0) return;

    const sectionIds = getSectionIdsToUpdate();
    let lastResponse = null;

    for (const variantId of variantIds) {
      const giftLine = findGiftLine(cart, variantId);

      // A variant only qualifies for the active promo if it has tracked paid
      // quantity right now. Anything else (promo disabled, product changed,
      // paid quantity removed) is treated as an orphan and zeroed out below.
      const paidQuantity = paidQuantityByVariant.get(variantId);
      const desiredGiftQuantity = paidQuantity !== undefined ? Math.min(paidQuantity, Theme.gwpPromo.maxFreeQuantity) : 0;
      const currentGiftQuantity = giftLine?.quantity ?? 0;

      if (desiredGiftQuantity === currentGiftQuantity) continue;

      if (giftLine) {
        lastResponse = await changeGiftLineQuantity({ line: giftLine, quantity: desiredGiftQuantity, sectionIds });
      } else if (desiredGiftQuantity > 0) {
        lastResponse = await addGiftLine({ variantId, quantity: desiredGiftQuantity, sectionIds });
      }
    }

    if (lastResponse) {
      await handleCartMutated(lastResponse);
    }
  } catch (error) {
    console.error(error);
  } finally {
    syncing = false;
    if (queued) {
      queued = false;
      syncGiftLines();
    }
  }
}

/**
 * @param {any} mutationResponse - The JSON response from the last change/add call.
 */
async function handleCartMutated(mutationResponse) {
  const sections = mutationResponse.sections;
  const freshCart = await fetchCart();

  document.dispatchEvent(
    new CartUpdateEvent(freshCart, GWP_SOURCE, {
      itemCount: freshCart.item_count,
      source: GWP_SOURCE,
      sections,
    })
  );
}

const debouncedSyncGiftLines = debounce(syncGiftLines, 300);

/**
 * @param {CartUpdateEvent} event
 */
function onCartUpdate(event) {
  if (event.detail?.data?.source === GWP_SOURCE) return;
  debouncedSyncGiftLines();
}

document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);

// Reconcile once on load: catches a cart that already has mismatched
// quantities (promo just enabled), or a lingering gift line that needs
// cleanup (promo just disabled).
Theme.utilities.scheduler.schedule(syncGiftLines);
